// src/components/roads/RoadCompletedWorkPanel.js
// --- UPDATE (Road Works Report — Completed work) -----------------------------
// "Completed work" tab of the Road Works Report: everything the field crews finished on this
// road, with the photos the FlowSense Tech app uploaded and the full job / inspection record.
//
//   <RoadCompletedWorkPanel roadId roadName? road? knownWorkOrderIds? focusJobId? onFocusHandled?
//        onOpenChecklist?({ checklistId, workOrder }) onDataChanged? />
//
//   Data: GET /roads/:id/completed-work (jobs + submitted inspections, photos by stage,
//   checklists with item photos, QA audits, asphalt, parts used, evidence gaps).
//   • KPIs: completed jobs, inspections, field photos, average time to complete, on-time %,
//     average evidence score.
//   • FlowSense AI: explainable evidence score per job (every deduction listed), findings
//     (no "after" photo, checklist not submitted, paving without QA, failed QA…), "Explain"
//     and "Explain this job" through FlowSense AI (module road_works) with a rules fallback.
//   • Timeline of jobs + inspections: filters (type, period, category, needs attention only),
//     search, CSV export, photo strip on every card.
//   • Detail drawer: resolution notes, every date / duration, technician, before ↔ after
//     comparison, photo galleries by stage and by checklist item (lightbox with GPS / time /
//     uploader), checklist findings, QA audit, asphalt, parts and cost; opens the full checklist
//     and the work order.
//   • Live: refreshes on road / work order WebSocket events for this road (debounced).
//   Nothing is invented — a value the server does not have shows "—".
// --- END UPDATE ---------------------------------------------------------------
import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Flex, HStack, VStack, Text, Badge, Button, IconButton, Icon, Input, InputGroup, InputLeftElement, Select,
    SimpleGrid, Tooltip, Alert, AlertIcon, AlertDescription, Skeleton, Stack, Checkbox, Divider,
    Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton, DrawerFooter,
    Table, Thead, Tbody, Tr, Th, Td, ButtonGroup, Collapse,
    useColorModeValue, useBreakpointValue, useToast,
} from '@chakra-ui/react';
import {
    MdTaskAlt, MdChecklist, MdPhotoCamera, MdSchedule, MdVerified, MdAutoAwesome, MdRefresh, MdSearch, MdDownload,
    MdOpenInNew, MdPlace, MdPerson, MdBuild, MdLocalShipping, MdInventory2, MdNotes, MdCompare,
} from 'react-icons/md';
import { apiFetch } from './page/shared';
import useFlowSenseLive from '../../hooks/useFlowSenseLive';
import { askFlowSenseAi } from '../../services/flowsenseAiService';
import ViewWorkOrderModal from '../work_orders/ViewWorkOrderModal';
import { PhotoThumb, PhotoLightbox } from './RoadInspectionChecklist';
import {
    analyzeCompletedWork, evidenceScore, explainJobLocally, answerCompletedWorkLocally, buildCompletedWorkAiContext,
    filterTimeline, timelineToCsv, formatDuration, GAP_LABEL,
} from './roadCompletedWorkIntel';

const PERIODS = [
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: '365', label: 'Last 12 months' },
    { value: '', label: 'All time' },
];
const STAGES = [
    { key: 'before', label: 'Before', color: 'orange' },
    { key: 'during', label: 'During', color: 'blue' },
    { key: 'after', label: 'After', color: 'green' },
    { key: 'inspection', label: 'Inspection', color: 'purple' },
];
const STAGE_LABEL = Object.fromEntries(STAGES.map((s) => [s.key, s.label]));
const RATING_COLOR = { excellent: 'green', good: 'teal', fair: 'yellow', poor: 'orange', critical: 'red' };
const QA_COLOR = { approved: 'green', flagged_for_penalty: 'orange', rework_mandated: 'red' };
const LEVEL_STATUS = { critical: 'error', high: 'warning', medium: 'warning', info: 'info' };
const EMPTY = Object.freeze([]);

const fmtWhen = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};
const fmtDay = (v) => {
    if (!v) return '—';
    const s = String(v);
    const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(s);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};
const money = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const human = (v) => (v ? String(v).replace(/_/g, ' ') : '—');
const hasGps = (lat, lng) => Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);

/** Every photo of a job in viewing order: before → during → after → inspection → checklist items. */
const jobPhotoList = (job) => [
    ...STAGES.flatMap((s) => job?.photos?.[s.key] || []),
    ...(job?.checklists || []).flatMap((c) => c.photos || []),
];
const stripFor = (job) => {
    const p = job?.photos || {};
    const out = [...(p.before || []).slice(0, 1), ...(p.after || []).slice(-2), ...(p.during || []).slice(0, 1)];
    if (out.length < 4) out.push(...(job?.checklists || []).flatMap((c) => c.photos || []).slice(0, 4 - out.length));
    return out.slice(0, 4);
};

// ─────────────────────────────────────────────────────────────────────────────
// Small presentational pieces
// ─────────────────────────────────────────────────────────────────────────────
const Kpi = memo(({ label, value, help, color = 'blue', icon, colors }) => (
    <Box p={3} borderWidth="1px" borderColor={colors.border} borderRadius="xl" bg={colors.cardBg} minW={0}>
        <HStack spacing={1.5} mb={1}>
            {icon && <Icon as={icon} color={`${color}.400`} boxSize={3.5} />}
            <Text fontSize="2xs" fontWeight="700" color={colors.muted} textTransform="uppercase" letterSpacing="wider" noOfLines={1}>{label}</Text>
        </HStack>
        <Text fontSize="xl" fontWeight="900" lineHeight="1.1">{value ?? '—'}</Text>
        {help && <Text fontSize="2xs" color={colors.muted} noOfLines={1}>{help}</Text>}
    </Box>
));
Kpi.displayName = 'Kpi';
Kpi.propTypes = { label: PropTypes.string, value: PropTypes.node, help: PropTypes.node, color: PropTypes.string, icon: PropTypes.any, colors: PropTypes.object.isRequired };

const Field = ({ label, children, colors }) => (
    <Box minW={0}>
        <Text fontSize="2xs" fontWeight="700" color={colors.muted} textTransform="uppercase" letterSpacing="wider">{label}</Text>
        <Box fontSize="sm" fontWeight="600">{children ?? '—'}</Box>
    </Box>
);
Field.propTypes = { label: PropTypes.string, children: PropTypes.node, colors: PropTypes.object.isRequired };

const Section = ({ icon, title, right, children, colors }) => (
    <Box borderWidth="1px" borderColor={colors.border} borderRadius="xl" p={4}>
        <Flex justify="space-between" align="center" mb={3} gap={2} wrap="wrap">
            <HStack spacing={2}>{icon && <Icon as={icon} color="blue.400" />}<Text fontWeight="800" fontSize="sm">{title}</Text></HStack>
            {right}
        </Flex>
        {children}
    </Box>
);
Section.propTypes = { icon: PropTypes.any, title: PropTypes.node, right: PropTypes.node, children: PropTypes.node, colors: PropTypes.object.isRequired };

const EvidenceBadge = ({ job }) => {
    const ev = evidenceScore(job);
    if (ev.score === null) return null;
    return (
        <Tooltip hasArrow label={ev.reasons.length ? ev.reasons.map((r) => `${r.points} ${r.why}`).join(' · ') : 'Photos, checklist and notes are all in place.'}>
            <Badge colorScheme={ev.level.color} cursor="help" fontSize="2xs" aria-label={`Evidence score ${ev.score}`}>
                <Icon as={MdVerified} mr={0.5} verticalAlign="-1px" />{ev.score} · {ev.level.label}
            </Badge>
        </Tooltip>
    );
};
EvidenceBadge.propTypes = { job: PropTypes.object.isRequired };

const PhotoGrid = ({ photos, onOpen, colors, labelFor, columns = { base: 3, sm: 4, md: 5 }, testId }) => (
    <SimpleGrid columns={columns} spacing={2} data-testid={testId}>
        {photos.map((p) => (
            <Box key={p.id} minW={0}>
                <PhotoThumb photo={p} size="100%" onOpen={onOpen} label={labelFor ? labelFor(p) : undefined} colors={colors} />
                {labelFor && labelFor(p) && <Text fontSize="2xs" color={colors.muted} noOfLines={1} mt={0.5} title={labelFor(p)}>{labelFor(p)}</Text>}
            </Box>
        ))}
    </SimpleGrid>
);
PhotoGrid.propTypes = { photos: PropTypes.array.isRequired, onOpen: PropTypes.func.isRequired, colors: PropTypes.object.isRequired, labelFor: PropTypes.func, columns: PropTypes.any, testId: PropTypes.string };

const AiAnswer = ({ answer, bg }) => (answer ? (
    <Box p={3} borderRadius="md" bg={bg} aria-live="polite" data-testid="cw-ai-answer">
        <HStack justify="flex-end" mb={1}>
            <Badge colorScheme={answer.source === 'ollama' ? 'purple' : 'gray'} fontSize="2xs">{answer.source === 'ollama' ? `FlowSense AI${answer.model ? ` · ${answer.model}` : ''}` : 'FlowSense rules'}</Badge>
        </HStack>
        <Text fontSize="xs" whiteSpace="pre-wrap">{answer.text}</Text>
    </Box>
) : null);
AiAnswer.propTypes = { answer: PropTypes.object, bg: PropTypes.string };

// ─────────────────────────────────────────────────────────────────────────────
// Timeline cards
// ─────────────────────────────────────────────────────────────────────────────
const JobCard = memo(({ job, onOpen, onOpenPhoto, colors }) => {
    const strip = stripFor(job);
    const total = jobPhotoList(job).length;
    const gaps = (job.gaps || []).filter((g) => g.code !== 'no_completed_at');
    const qa = (job.qa || [])[0];
    return (
        <Box p={3} borderWidth="1px" borderColor={gaps.length ? colors.warnBorder : colors.border} borderRadius="xl" bg={colors.cardBg} data-testid={`job-${job.id}`}>
            <Flex gap={3} align="flex-start" wrap={{ base: 'wrap', md: 'nowrap' }}>
                <Box bg="green.500" color="white" p={2} borderRadius="lg" flexShrink={0}><Icon as={MdTaskAlt} boxSize={4} /></Box>
                <Box flex="1" minW="200px">
                    <HStack spacing={1.5} flexWrap="wrap">
                        <Text fontWeight="800" fontSize="sm">{job.title}</Text>
                        {job.category && <Badge variant="subtle" fontSize="2xs">{job.category}</Badge>}
                        {job.work_order_type && <Badge colorScheme="blue" variant="outline" fontSize="2xs">{job.work_order_type}</Badge>}
                        {job.priority && <Badge colorScheme={job.priority === 'critical' ? 'red' : job.priority === 'high' ? 'orange' : 'gray'} fontSize="2xs">{job.priority}</Badge>}
                        <EvidenceBadge job={job} />
                    </HStack>
                    <Text fontSize="xs" color={colors.muted} mt={0.5}>
                        {job.completed_at_recorded ? 'Completed' : 'Last updated'} {fmtWhen(job.finished_at)}
                        {' • '}<Icon as={MdPerson} verticalAlign="-2px" /> {job.assigned_user?.name || job.assigned_user_username || 'Unassigned'}
                        {job.hours_open !== null && ` • ${formatDuration(job.hours_open)} open`}
                    </Text>
                    <HStack spacing={1} mt={1.5} flexWrap="wrap">
                        {job.on_time === true && <Badge colorScheme="green" fontSize="2xs">On time</Badge>}
                        {job.on_time === false && <Badge colorScheme="red" variant="outline" fontSize="2xs">Late</Badge>}
                        <Tooltip hasArrow label="Field-app photos: before · during · after · inspection">
                            <Badge colorScheme="purple" variant="subtle" fontSize="2xs" aria-label={`${total} photos`}>
                                <Icon as={MdPhotoCamera} mr={0.5} verticalAlign="-1px" />
                                B {job.photo_counts?.before ?? 0} · D {job.photo_counts?.during ?? 0} · A {job.photo_counts?.after ?? 0} · I {job.photo_counts?.inspection ?? 0}
                            </Badge>
                        </Tooltip>
                        {job.checklist && <Badge colorScheme={RATING_COLOR[job.checklist.rating] || 'gray'} fontSize="2xs">Checklist {job.checklist.score ?? '—'}</Badge>}
                        {qa && <Badge colorScheme={QA_COLOR[qa.ai_approval_status] || 'gray'} fontSize="2xs">QA {qa.ai_quality_score ?? '—'} · {human(qa.ai_approval_status)}</Badge>}
                        {gaps.map((g) => <Badge key={g.code} colorScheme="orange" variant="outline" fontSize="2xs">{GAP_LABEL[g.code] || g.message}</Badge>)}
                    </HStack>
                    {job.resolution_notes && <Text fontSize="xs" mt={1.5} noOfLines={2} color={colors.text}>“{job.resolution_notes}”</Text>}
                </Box>
                <VStack align={{ base: 'flex-start', md: 'flex-end' }} spacing={2} flexShrink={0}>
                    {strip.length > 0 && (
                        <HStack spacing={1.5}>
                            {strip.map((p) => (
                                <Box key={p.id} position="relative">
                                    <PhotoThumb photo={p} size="56px" onOpen={(ph) => onOpenPhoto(job, ph)} badge={p.checklist_id ? undefined : STAGE_LABEL[p.stage]} colors={colors} />
                                </Box>
                            ))}
                            {total > strip.length && <Text fontSize="2xs" color={colors.muted}>+{total - strip.length}</Text>}
                        </HStack>
                    )}
                    <Button size="xs" colorScheme="blue" variant="outline" onClick={() => onOpen({ kind: 'job', id: job.id })} aria-label={`Details for ${job.title}`}>
                        Details & photos
                    </Button>
                </VStack>
            </Flex>
        </Box>
    );
});
JobCard.displayName = 'JobCard';
JobCard.propTypes = { job: PropTypes.object.isRequired, onOpen: PropTypes.func.isRequired, onOpenPhoto: PropTypes.func.isRequired, colors: PropTypes.object.isRequired };

const InspectionCard = memo(({ inspection: i, onOpen, onOpenPhoto, colors }) => {
    const strip = (i.photos || []).slice(0, 4);
    return (
        <Box p={3} borderWidth="1px" borderColor={colors.border} borderRadius="xl" bg={colors.cardBg} data-testid={`inspection-${i.id}`}>
            <Flex gap={3} align="flex-start" wrap={{ base: 'wrap', md: 'nowrap' }}>
                <Box bg="purple.500" color="white" p={2} borderRadius="lg" flexShrink={0}><Icon as={MdChecklist} boxSize={4} /></Box>
                <Box flex="1" minW="200px">
                    <HStack spacing={1.5} flexWrap="wrap">
                        <Text fontWeight="800" fontSize="sm">{i.template_label || i.template_key}</Text>
                        <Badge colorScheme={RATING_COLOR[i.rating] || 'gray'} fontSize="2xs">{i.score ?? '—'}{i.rating ? ` · ${i.rating}` : ''}</Badge>
                        {i.counts?.failed > 0 && <Badge colorScheme="red" variant="outline" fontSize="2xs">{i.counts.failed} defect(s)</Badge>}
                        <Badge colorScheme="purple" variant="subtle" fontSize="2xs"><Icon as={MdPhotoCamera} mr={0.5} verticalAlign="-1px" />{i.photo_count}</Badge>
                    </HStack>
                    <Text fontSize="xs" color={colors.muted} mt={0.5}>
                        Submitted {fmtWhen(i.submitted_at)} • <Icon as={MdPerson} verticalAlign="-2px" /> {i.submitted_by?.name || (i.submitted_by?.id ? `User ${i.submitted_by.id}` : '—')}
                        {i.work_order_title ? ` • WO ${i.work_order_title}` : ''}
                        {i.outcome?.auto_work_order_id ? ` • raised ${i.outcome.auto_work_order_id}` : ''}
                    </Text>
                    {i.failed_items?.length > 0 && (
                        <Text fontSize="xs" mt={1} noOfLines={2}>✗ {i.failed_items.slice(0, 3).map((f) => `${f.label}${f.note ? ` — ${f.note}` : ''}`).join(' · ')}</Text>
                    )}
                </Box>
                <VStack align={{ base: 'flex-start', md: 'flex-end' }} spacing={2} flexShrink={0}>
                    {strip.length > 0 && (
                        <HStack spacing={1.5}>
                            {strip.map((p) => <PhotoThumb key={p.id} photo={p} size="56px" onOpen={(ph) => onOpenPhoto(i, ph)} label={p.item_label || undefined} colors={colors} />)}
                            {i.photo_count > strip.length && <Text fontSize="2xs" color={colors.muted}>+{i.photo_count - strip.length}</Text>}
                        </HStack>
                    )}
                    <Button size="xs" colorScheme="purple" variant="outline" onClick={() => onOpen({ kind: 'inspection', id: i.id })} aria-label={`Details for inspection ${i.id}`}>
                        Details & photos
                    </Button>
                </VStack>
            </Flex>
        </Box>
    );
});
InspectionCard.displayName = 'InspectionCard';
InspectionCard.propTypes = { inspection: PropTypes.object.isRequired, onOpen: PropTypes.func.isRequired, onOpenPhoto: PropTypes.func.isRequired, colors: PropTypes.object.isRequired };

// ─────────────────────────────────────────────────────────────────────────────
// Checklist findings (used for job checklists and road inspections)
// ─────────────────────────────────────────────────────────────────────────────
const ChecklistFindings = ({ checklist: c, onOpenPhoto, onOpenChecklist, colors }) => (
    <VStack align="stretch" spacing={3}>
        <HStack spacing={2} flexWrap="wrap">
            <Text fontWeight="700" fontSize="sm">{c.template_label || c.template_key}</Text>
            <Badge colorScheme={c.status === 'submitted' ? 'green' : 'yellow'} fontSize="2xs">{c.status}</Badge>
            <Badge colorScheme={RATING_COLOR[c.rating] || 'gray'} fontSize="2xs">{c.score ?? '—'}{c.rating ? ` · ${c.rating}` : ''}</Badge>
            {c.counts && <Text fontSize="xs" color={colors.muted}>{c.counts.passed} OK · {c.counts.failed} defect(s) · {c.counts.na} N/A of {c.counts.total}</Text>}
            {onOpenChecklist && <Button size="xs" variant="link" colorScheme="blue" rightIcon={<Icon as={MdOpenInNew} />} onClick={() => onOpenChecklist(c)}>Open full checklist</Button>}
        </HStack>
        <Text fontSize="xs" color={colors.muted}>
            Submitted {fmtWhen(c.submitted_at)} by {c.submitted_by?.name || (c.submitted_by?.id ? `User ${c.submitted_by.id}` : '—')}
        </Text>
        {c.failed_items?.length > 0 ? (
            <VStack align="stretch" spacing={1.5}>
                {c.failed_items.map((f) => (
                    <Box key={f.id} p={2} borderRadius="md" bg={colors.failBg} borderWidth="1px" borderColor={colors.failBorder}>
                        <HStack spacing={1.5} flexWrap="wrap">
                            <Text fontSize="xs" fontWeight="700">✗ {f.label}</Text>
                            {f.critical && <Badge colorScheme="red" fontSize="2xs">critical</Badge>}
                            {f.out_of_range && <Badge colorScheme="red" variant="outline" fontSize="2xs">out of spec</Badge>}
                            {f.value !== null && f.value !== undefined && <Text fontSize="2xs" fontFamily="mono">{f.value}{f.unit ? ` ${f.unit}` : ''}</Text>}
                            <Badge colorScheme="purple" variant="subtle" fontSize="2xs"><Icon as={MdPhotoCamera} mr={0.5} verticalAlign="-1px" />{f.photo_count}</Badge>
                        </HStack>
                        {f.note && <Text fontSize="xs" mt={0.5}>{f.note}</Text>}
                    </Box>
                ))}
            </VStack>
        ) : <Text fontSize="xs" color="green.500">No defects recorded.</Text>}
        {c.notes_by_item?.length > 0 && (
            <VStack align="stretch" spacing={0.5}>
                <Text fontSize="2xs" fontWeight="700" color={colors.muted} textTransform="uppercase">Item notes</Text>
                {c.notes_by_item.map((n) => <Text key={n.id} fontSize="xs"><b>{n.label}:</b> {n.note}</Text>)}
            </VStack>
        )}
        {c.overall_notes && <Box><Text fontSize="2xs" fontWeight="700" color={colors.muted} textTransform="uppercase">Inspector notes</Text><Text fontSize="sm" whiteSpace="pre-wrap">{c.overall_notes}</Text></Box>}
        {(c.feedback?.rating || c.feedback?.comments || c.feedback?.crew_feedback || c.feedback?.public_impact || c.feedback?.weather) && (
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                <Field label="Overall rating" colors={colors}>{c.feedback.rating ? `${'★'.repeat(c.feedback.rating)}${'☆'.repeat(5 - c.feedback.rating)}` : '—'}</Field>
                <Field label="Impact on the public" colors={colors}>{human(c.feedback.public_impact)}</Field>
                <Field label="Weather / surface" colors={colors}>{c.feedback.weather || '—'}</Field>
                <Field label="Feedback" colors={colors}>{c.feedback.comments || '—'}</Field>
                <Field label="Crew feedback" colors={colors}>{c.feedback.crew_feedback || '—'}</Field>
            </SimpleGrid>
        )}
        {c.photos?.length > 0 ? (
            <Box>
                <Text fontSize="2xs" fontWeight="700" color={colors.muted} textTransform="uppercase" mb={1}>Inspection photos ({c.photos.length})</Text>
                <PhotoGrid photos={c.photos} onOpen={onOpenPhoto} colors={colors} labelFor={(p) => p.item_label || p.caption || 'General'} testId={`checklist-photos-${c.id}`} />
            </Box>
        ) : <Text fontSize="xs" color={colors.muted}>No photos were uploaded for this checklist.</Text>}
    </VStack>
);
ChecklistFindings.propTypes = { checklist: PropTypes.object.isRequired, onOpenPhoto: PropTypes.func.isRequired, onOpenChecklist: PropTypes.func, colors: PropTypes.object.isRequired };

// ─────────────────────────────────────────────────────────────────────────────
// Main panel
// ─────────────────────────────────────────────────────────────────────────────
function RoadCompletedWorkPanel({ roadId, roadName, road, knownWorkOrderIds, focusJobId, onFocusHandled, onOpenChecklist, onDataChanged }) {
    const toast = useToast();
    const drawerSize = useBreakpointValue({ base: 'full', md: 'xl', xl: 'xl' });
    const muted = useColorModeValue('gray.500', 'gray.400');
    const text = useColorModeValue('gray.700', 'gray.200');
    const border = useColorModeValue('gray.200', 'gray.700');
    const cardBg = useColorModeValue('white', 'gray.800');
    const warnBorder = useColorModeValue('orange.200', 'orange.700');
    const thumbBg = useColorModeValue('gray.100', 'whiteAlpha.100');
    const lightboxBg = useColorModeValue('white', 'gray.800');
    const failBg = useColorModeValue('red.50', 'rgba(245,101,101,0.10)');
    const failBorder = useColorModeValue('red.200', 'red.700');
    const aiBg = useColorModeValue('purple.50', 'rgba(128,90,213,0.12)');
    const filterBg = useColorModeValue('gray.50', 'gray.900');
    const colors = useMemo(() => ({ muted, text, border, cardBg, warnBorder, thumbBg, lightboxBg, failBg, failBorder }),
        [muted, text, border, cardBg, warnBorder, thumbBg, lightboxBg, failBg, failBorder]);

    const [data, setData] = useState(null);
    const [state, setState] = useState({ loading: true, refreshing: false, error: null, at: null });
    const [warnings, setWarnings] = useState([]);
    const [period, setPeriod] = useState('365');
    const [kind, setKind] = useState('all');
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('');
    const [gapsOnly, setGapsOnly] = useState(false);
    const [selected, setSelected] = useState(null);       // { kind, id }
    const [lightbox, setLightbox] = useState(null);       // { photos, index }
    const [ai, setAi] = useState({ busy: null, answer: null }); // answer: { scope, text, source, model }
    const [woModalJob, setWoModalJob] = useState(null);
    const [showFindings, setShowFindings] = useState(true);
    const seqRef = useRef(0);
    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

    const load = useCallback(async (silent = false) => {
        if (!roadId) return;
        const seq = ++seqRef.current;
        setState((s) => ({ ...s, loading: !silent && !s.at, refreshing: silent || !!s.at, error: silent ? s.error : null }));
        try {
            const q = new URLSearchParams({ limit: '100' });
            if (period) q.set('days', period);
            const res = await apiFetch(`/roads/${encodeURIComponent(roadId)}/completed-work?${q.toString()}`);
            if (!mountedRef.current || seq !== seqRef.current) return;
            setData(res?.data || { jobs: [], inspections: [], summary: {}, users: {} });
            setWarnings(Array.isArray(res?.warnings) ? res.warnings : []);
            setState({ loading: false, refreshing: false, error: null, at: new Date(), period });
        } catch (err) {
            if (!mountedRef.current || seq !== seqRef.current) return;
            setState((s) => ({ ...s, loading: false, refreshing: false, error: err?.message || 'Could not load the completed work.' }));
        }
    }, [roadId, period]);

    // road switch → clean slate
    useEffect(() => {
        setData(null); setSelected(null); setLightbox(null); setAi({ busy: null, answer: null }); setCategory(''); setSearch('');
        setState({ loading: true, refreshing: false, error: null, at: null });
    }, [roadId]);
    useEffect(() => { load(false); }, [load]);

    // Live: a job finished / photos added / inspection submitted on this road
    const jobIdsRef = useRef(new Set());
    jobIdsRef.current = new Set([...(data?.jobs || []).map((j) => String(j.id)), ...((knownWorkOrderIds || []).map(String))]);
    const liveFilter = useCallback((name, msg) => {
        const p = msg?.payload || msg?.data || msg || {};
        const rid = p.road_id ?? p.roadId ?? p.asset_id ?? null;
        if (rid !== null && rid !== undefined) return String(rid) === String(roadId);
        const wo = p.workOrderId ?? p.work_order_id;
        if (wo !== undefined && wo !== null) return jobIdsRef.current.has(String(wo));
        return name.startsWith('bus:');
    }, [roadId]);
    useFlowSenseLive(['roads', 'workOrders'], () => load(true), { debounceMs: 1500, filter: liveFilter, enabled: !!roadId });

    const analysis = useMemo(() => (data ? analyzeCompletedWork(data) : null), [data]);
    const categories = useMemo(() => Object.keys(data?.summary?.by_category || {}).sort(), [data]);
    const rows = useMemo(() => (data ? filterTimeline(data, { kind, search, category, gapsOnly }) : EMPTY), [data, kind, search, category, gapsOnly]);
    const selectedJob = useMemo(() => (selected?.kind === 'job' ? (data?.jobs || []).find((j) => j.id === selected.id) || null : null), [selected, data]);
    const selectedInsp = useMemo(() => (selected?.kind === 'inspection' ? (data?.inspections || []).find((i) => i.id === selected.id) || null : null), [selected, data]);

    // Linked work orders tab → "view completed work" for one job
    useEffect(() => {
        if (!focusJobId || !data || state.loading || state.period !== period) return; // wait for this period's data
        const hit = (data.jobs || []).find((j) => String(j.id) === String(focusJobId));
        if (!hit && period) { setPeriod(''); return; } // older job → widen to all time, then look again
        if (hit) setSelected({ kind: 'job', id: hit.id });
        else toast({ title: 'Completed job not found', description: 'It may have been reopened or is linked to another road.', status: 'info', duration: 3000 });
        onFocusHandled?.();
    }, [focusJobId, data, state.loading, state.period, period]); // eslint-disable-line react-hooks/exhaustive-deps

    const users = useMemo(() => data?.users || {}, [data]);
    const uploaderName = useCallback((id) => {
        if (id === null || id === undefined || id === '') return '—';
        return users[String(id)]?.name || users[String(id)]?.username || `User ${id}`;
    }, [users]);
    const photoMeta = useCallback((p) => (p.checklist_id
        ? ['Checklist item', p.item_label || 'General inspection photo']
        : ['Work order photo', STAGE_LABEL[p.stage] || human(p.stage)]), []);
    const openPhotos = useCallback((photos, photo) => {
        const idx = photos.findIndex((x) => x.id === photo.id);
        setLightbox({ photos, index: Math.max(0, idx) });
    }, []);
    const openCardPhoto = useCallback((owner, photo) => {
        openPhotos(owner.photos && !Array.isArray(owner.photos) ? jobPhotoList(owner) : (owner.photos || []), photo);
    }, [openPhotos]);

    const askAi = useCallback(async (job = null) => {
        if (!data) return;
        const scope = job ? `job:${job.id}` : 'panel';
        setAi({ busy: scope, answer: null });
        const ctx = buildCompletedWorkAiContext({ ...data, road: data.road || { id: roadId, name: roadName } }, job);
        const q = job
            ? `Explain what was done on this completed road job for ${roadName || 'the road'}: the work, the evidence (photos, checklist, QA), whether it can be signed off and anything still missing.`
            : `Summarise the completed road work and inspections on ${roadName || 'this road'}: what was done, how well it is evidenced with photos and checklists, and what a supervisor should check before sign-off.`;
        try {
            const r = await askFlowSenseAi(q, ctx, 'road_works');
            if (!r?.answer) throw new Error('empty');
            if (mountedRef.current) setAi({ busy: null, answer: { scope, text: r.answer, source: 'ollama', model: r.model } });
        } catch {
            const text = job ? explainJobLocally(job) : answerCompletedWorkLocally('summary', data).answer;
            if (mountedRef.current) setAi({ busy: null, answer: { scope, text, source: 'rules' } });
        }
    }, [data, roadId, roadName]);

    const exportCsv = useCallback(() => {
        try {
            const csv = timelineToCsv(rows, roadName || data?.road?.name || '');
            const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `completed-work-${String(roadName || roadId).replace(/[^\w-]+/g, '_')}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            toast({ title: 'Export failed', description: e?.message, status: 'error', duration: 3000 });
        }
    }, [rows, roadName, roadId, data, toast]);

    const openChecklist = useCallback((c, job = null) => {
        if (!onOpenChecklist) return;
        setSelected(null);
        onOpenChecklist({ checklistId: c.id, workOrder: job ? { ...job, assigned_user_username: job.assigned_user?.name || job.assigned_user_username } : undefined });
    }, [onOpenChecklist]);

    const s = analysis?.stats;
    const aiAnswerFor = (scope) => (ai.answer?.scope === scope ? ai.answer : null);

    if (!roadId) return <Text p={4} fontSize="xs" color={muted}>Select a road to see its completed work.</Text>;

    return (
        <VStack align="stretch" spacing={4} data-testid="completed-work-panel">
            {state.error && (
                <Alert status="error" borderRadius="lg">
                    <AlertIcon />
                    <AlertDescription flex={1} fontSize="sm">{state.error}</AlertDescription>
                    <Button size="xs" onClick={() => load(false)}>Retry</Button>
                </Alert>
            )}
            {warnings.length > 0 && (
                <Alert status="info" borderRadius="lg" py={2} fontSize="xs">
                    <AlertIcon boxSize={3.5} />
                    <AlertDescription>Some sections are not available on this server yet: {warnings.map((w) => w.replace(' unavailable', '')).join(', ')}.</AlertDescription>
                </Alert>
            )}

            {state.loading && !data ? (
                <Stack spacing={3} aria-label="Loading completed work">
                    <Skeleton h="70px" borderRadius="xl" />
                    <Skeleton h="120px" borderRadius="xl" />
                </Stack>
            ) : data && (
                <>
                    {/* KPIs */}
                    <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3}>
                        <Kpi label="Completed jobs" value={s.jobs} icon={MdTaskAlt} color="green" help={categories.length ? `${categories.length} categor${categories.length === 1 ? 'y' : 'ies'}` : null} colors={colors} />
                        <Kpi label="Inspections" value={s.inspections} icon={MdChecklist} color="purple" help={s.inspectionAvg !== null ? `avg score ${s.inspectionAvg}` : null} colors={colors} />
                        <Kpi label="Field photos" value={s.photos} icon={MdPhotoCamera} color="purple" help={s.noAfter ? `${s.noAfter} job(s) without "after"` : 'from the Tech app'} colors={colors} />
                        <Kpi label="Avg time to complete" value={formatDuration(s.avgHours)} icon={MdSchedule} color="blue" help="created → completed" colors={colors} />
                        <Kpi label="On time" value={s.onTimePct !== null ? `${s.onTimePct}%` : '—'} icon={MdSchedule} color="teal" help={data.summary?.on_time_known ? `${data.summary.on_time_known} with a due date` : 'no due dates'} colors={colors} />
                        <Kpi label="Evidence score" value={s.avgEvidence !== null ? `${s.avgEvidence}` : '—'} icon={MdVerified} color={s.avgEvidence === null ? 'gray' : s.avgEvidence >= 85 ? 'green' : s.avgEvidence >= 60 ? 'yellow' : 'red'} help="photos · checklist · notes · QA" colors={colors} />
                    </SimpleGrid>

                    {/* FlowSense AI */}
                    <Box p={4} borderRadius="xl" bg={aiBg} role="region" aria-label="Completed work AI helper">
                        <Flex justify="space-between" align="center" gap={2} wrap="wrap" mb={2}>
                            <HStack spacing={2}><Icon as={MdAutoAwesome} color="purple.400" /><Text fontWeight="800" fontSize="sm">FlowSense AI — sign-off check</Text></HStack>
                            <HStack spacing={2}>
                                {analysis.findings.length > 0 && <Button size="xs" variant="ghost" onClick={() => setShowFindings((v) => !v)}>{showFindings ? 'Hide' : `Show ${analysis.findings.length}`} findings</Button>}
                                <Button size="xs" colorScheme="purple" leftIcon={<Icon as={MdAutoAwesome} />} onClick={() => askAi(null)} isLoading={ai.busy === 'panel'} loadingText="Asking…">Explain completed work</Button>
                            </HStack>
                        </Flex>
                        <Text fontSize="sm" mb={2}>{analysis.headline}</Text>
                        <Collapse in={showFindings} animateOpacity>
                            <VStack align="stretch" spacing={1.5}>
                                {analysis.findings.map((f) => (
                                    <Alert key={f.id} status={LEVEL_STATUS[f.level] || 'info'} borderRadius="md" py={1.5} px={3} fontSize="xs">
                                        <AlertIcon boxSize={3.5} />
                                        <Box flex={1}><Text fontWeight="700">{f.title}</Text>{f.detail && <Text>{f.detail}</Text>}</Box>
                                        {(f.jobId || f.checklistId) && (
                                            <Button size="xs" variant="outline" onClick={() => setSelected(f.jobId ? { kind: 'job', id: f.jobId } : { kind: 'inspection', id: f.checklistId })}>Open</Button>
                                        )}
                                    </Alert>
                                ))}
                            </VStack>
                        </Collapse>
                        <Box mt={2}><AiAnswer answer={aiAnswerFor('panel')} bg={cardBg} /></Box>
                    </Box>

                    {/* Filters */}
                    <Flex gap={2} wrap="wrap" align="center" p={2} borderRadius="xl" bg={filterBg}>
                        <ButtonGroup size="xs" isAttached variant="outline" role="group" aria-label="Show">
                            {[['all', 'All'], ['jobs', `Jobs (${s.jobs})`], ['inspections', `Inspections (${s.inspections})`]].map(([k, l]) => (
                                <Button key={k} onClick={() => setKind(k)} variant={kind === k ? 'solid' : 'outline'} colorScheme={kind === k ? 'blue' : 'gray'} aria-pressed={kind === k}>{l}</Button>
                            ))}
                        </ButtonGroup>
                        <Select size="xs" maxW="150px" value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Period">
                            {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </Select>
                        <Select size="xs" maxW="190px" value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category" isDisabled={!categories.length}>
                            <option value="">All categories</option>
                            {categories.map((c) => <option key={c} value={c}>{c} ({data.summary.by_category[c]})</option>)}
                        </Select>
                        <InputGroup size="xs" maxW="240px">
                            <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color={muted} /></InputLeftElement>
                            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, notes, technician…" aria-label="Search completed work" borderRadius="md" />
                        </InputGroup>
                        <Checkbox size="sm" isChecked={gapsOnly} onChange={(e) => setGapsOnly(e.target.checked)}>
                            <Text fontSize="xs">Needs attention only</Text>
                        </Checkbox>
                        <HStack ml="auto" spacing={1}>
                            {state.at && <Text fontSize="2xs" color={muted}>Updated {state.at.toLocaleTimeString()}</Text>}
                            <Tooltip hasArrow label="Export this list (CSV)"><IconButton size="xs" variant="ghost" icon={<Icon as={MdDownload} />} onClick={exportCsv} isDisabled={!rows.length} aria-label="Export CSV" /></Tooltip>
                            <Tooltip hasArrow label="Refresh"><IconButton size="xs" variant="ghost" icon={<Icon as={MdRefresh} />} onClick={() => load(true)} isLoading={state.refreshing} aria-label="Refresh completed work" /></Tooltip>
                        </HStack>
                    </Flex>

                    {/* Timeline */}
                    <VStack align="stretch" spacing={2} data-testid="cw-timeline">
                        {rows.length === 0 && (
                            <Text p={4} fontSize="sm" color={muted} textAlign="center">
                                {s.jobs + s.inspections === 0
                                    ? `No completed jobs or submitted inspections on this road${period ? ' in this period' : ''}.`
                                    : 'Nothing matches these filters.'}
                            </Text>
                        )}
                        {rows.map((r) => (r.kind === 'job'
                            ? <JobCard key={`j-${r.id}`} job={r.job} onOpen={setSelected} onOpenPhoto={openCardPhoto} colors={colors} />
                            : <InspectionCard key={`i-${r.id}`} inspection={r.inspection} onOpen={setSelected} onOpenPhoto={openCardPhoto} colors={colors} />))}
                    </VStack>
                </>
            )}

            {/* ── Job detail ─────────────────────────────────────────────────── */}
            <Drawer isOpen={!!selectedJob} onClose={() => setSelected(null)} size={drawerSize} placement="right">
                <DrawerOverlay />
                <DrawerContent data-testid="cw-job-drawer">
                    <DrawerCloseButton />
                    {selectedJob && (() => {
                        const j = selectedJob;
                        const ev = evidenceScore(j);
                        const all = jobPhotoList(j);
                        const before = j.photos?.before?.[0];
                        const after = j.photos?.after?.length ? j.photos.after[j.photos.after.length - 1] : null;
                        const gaps = j.gaps || [];
                        return (
                            <>
                                <DrawerHeader borderBottomWidth="1px" pr={10}>
                                    <Text fontSize="md" fontWeight="800">{j.title}</Text>
                                    <HStack spacing={1.5} mt={1} flexWrap="wrap">
                                        <Text fontSize="xs" fontFamily="mono" color={muted}>{j.id}</Text>
                                        <Badge colorScheme="green">{j.status}</Badge>
                                        {j.category && <Badge variant="subtle">{j.category}</Badge>}
                                        {j.work_order_type && <Badge colorScheme="blue" variant="outline">{j.work_order_type}</Badge>}
                                        <EvidenceBadge job={j} />
                                    </HStack>
                                </DrawerHeader>
                                <DrawerBody py={4}>
                                    <VStack align="stretch" spacing={4}>
                                        {gaps.length > 0 && (
                                            <Alert status="warning" borderRadius="lg" alignItems="flex-start" fontSize="sm">
                                                <AlertIcon />
                                                <Box>
                                                    <Text fontWeight="700">Before sign-off</Text>
                                                    {gaps.map((g) => <Text key={g.code} fontSize="xs">• {g.message}</Text>)}
                                                </Box>
                                            </Alert>
                                        )}

                                        <Section icon={MdNotes} title="Work done" colors={colors}
                                            right={<Button size="xs" colorScheme="purple" variant="outline" leftIcon={<Icon as={MdAutoAwesome} />} onClick={() => askAi(j)} isLoading={ai.busy === `job:${j.id}`} loadingText="Asking…">Explain this job</Button>}>
                                            {j.resolution_notes
                                                ? <Text fontSize="sm" whiteSpace="pre-wrap" data-testid="cw-resolution">{j.resolution_notes}</Text>
                                                : <Text fontSize="sm" color="orange.400">The technician did not add resolution notes.</Text>}
                                            {j.description && (
                                                <Box mt={3}>
                                                    <Text fontSize="2xs" fontWeight="700" color={muted} textTransform="uppercase">Original request</Text>
                                                    <Text fontSize="xs" whiteSpace="pre-wrap" noOfLines={6}>{j.description}</Text>
                                                </Box>
                                            )}
                                            <Box mt={3}><AiAnswer answer={aiAnswerFor(`job:${j.id}`)} bg={aiBg} /></Box>
                                        </Section>

                                        <Section icon={MdSchedule} title="Details" colors={colors}>
                                            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                                                <Field label="Technician" colors={colors}>{j.assigned_user?.name || j.assigned_user_username || 'Unassigned'}</Field>
                                                <Field label="Created by" colors={colors}>{j.created_by?.name || '—'}</Field>
                                                <Field label="Priority" colors={colors}>{human(j.priority)}</Field>
                                                <Field label="Created" colors={colors}>{fmtWhen(j.created_at)}</Field>
                                                <Field label="Work started" colors={colors}>{fmtWhen(j.started_at)}</Field>
                                                <Field label={j.completed_at_recorded ? 'Completed' : 'Last updated'} colors={colors}>{fmtWhen(j.finished_at)}</Field>
                                                <Field label="Due" colors={colors}>{fmtDay(j.due_date)}</Field>
                                                <Field label="Time open" colors={colors}>{formatDuration(j.hours_open)}</Field>
                                                <Field label="Working time" colors={colors}>{formatDuration(j.hours_working)}</Field>
                                                <Field label="On time" colors={colors}>{j.on_time === null ? '—' : j.on_time ? <Badge colorScheme="green">Yes</Badge> : <Badge colorScheme="red">Late</Badge>}</Field>
                                                <Field label="SLA" colors={colors}>{human(j.sla_status)}</Field>
                                                <Field label="On hold" colors={colors}>{j.hold_minutes ? `${j.hold_minutes} min` : '—'}</Field>
                                                <Field label="Estimated cost" colors={colors}>{money(j.estimated_cost)}</Field>
                                                <Field label="Actual cost" colors={colors}>{money(j.actual_cost)}</Field>
                                                <Field label="Parts cost" colors={colors}>{money(j.parts_cost)}</Field>
                                                <Field label="Location" colors={colors}>
                                                    {hasGps(j.latitude, j.longitude) ? (
                                                        <Button as="a" size="xs" variant="link" leftIcon={<Icon as={MdPlace} />} href={`https://www.google.com/maps?q=${j.latitude},${j.longitude}`} target="_blank" rel="noopener noreferrer">
                                                            {j.location_description || `${j.latitude.toFixed(5)}, ${j.longitude.toFixed(5)}`}
                                                        </Button>
                                                    ) : (j.location_description || '—')}
                                                </Field>
                                                {j.parent_work_order_id && <Field label="Raised from" colors={colors}><Text fontFamily="mono" fontSize="xs">{j.parent_work_order_id}</Text></Field>}
                                            </SimpleGrid>
                                        </Section>

                                        {(before || after) && (
                                            <Section icon={MdCompare} title="Before ↔ after" colors={colors}>
                                                <SimpleGrid columns={2} spacing={3} maxW="560px" data-testid="cw-before-after">
                                                    {[['Before', before, 'orange'], ['After', after, 'green']].map(([label, p, c]) => (
                                                        <Box key={label}>
                                                            <Badge colorScheme={c} mb={1}>{label}</Badge>
                                                            {p ? <PhotoThumb photo={p} size="100%" onOpen={(ph) => openPhotos(all, ph)} label={label} colors={colors} />
                                                                : <Flex h="120px" align="center" justify="center" borderWidth="1px" borderStyle="dashed" borderColor={border} borderRadius="md"><Text fontSize="xs" color={muted}>No {label.toLowerCase()} photo</Text></Flex>}
                                                            {p && <Text fontSize="2xs" color={muted} mt={0.5}>{fmtWhen(p.taken_at || p.created_at)}</Text>}
                                                        </Box>
                                                    ))}
                                                </SimpleGrid>
                                            </Section>
                                        )}

                                        <Section icon={MdPhotoCamera} title={`Field photos (${all.length})`} colors={colors}>
                                            {all.length === 0 && <Text fontSize="sm" color={muted}>No photos were uploaded for this job.</Text>}
                                            <VStack align="stretch" spacing={3}>
                                                {STAGES.map((st) => (j.photos?.[st.key]?.length ? (
                                                    <Box key={st.key}>
                                                        <Badge colorScheme={st.color} mb={1.5}>{st.label} · {j.photos[st.key].length}</Badge>
                                                        <PhotoGrid photos={j.photos[st.key]} onOpen={(ph) => openPhotos(all, ph)} colors={colors} labelFor={(p) => p.caption || null} testId={`cw-stage-${st.key}`} />
                                                    </Box>
                                                ) : null))}
                                                {(j.checklists || []).filter((c) => c.photos?.length).map((c) => (
                                                    <Box key={c.id}>
                                                        <Badge colorScheme="purple" mb={1.5}>{c.template_label} · {c.photos.length}</Badge>
                                                        <PhotoGrid photos={c.photos} onOpen={(ph) => openPhotos(all, ph)} colors={colors} labelFor={(p) => p.item_label || p.caption || 'General'} />
                                                    </Box>
                                                ))}
                                            </VStack>
                                        </Section>

                                        {(j.checklists || []).length > 0 ? (
                                            <Section icon={MdChecklist} title="Acceptance checklist" colors={colors}>
                                                <VStack align="stretch" spacing={4} divider={<Divider />}>
                                                    {j.checklists.map((c) => (
                                                        <ChecklistFindings key={c.id} checklist={c} colors={colors} onOpenPhoto={(ph) => openPhotos(c.photos, ph)}
                                                            onOpenChecklist={onOpenChecklist ? (ck) => openChecklist(ck, j) : undefined} />
                                                    ))}
                                                </VStack>
                                            </Section>
                                        ) : null}

                                        {(j.qa || []).length > 0 && (
                                            <Section icon={MdVerified} title="QA workmanship audit" colors={colors}>
                                                <VStack align="stretch" spacing={3} divider={<Divider />}>
                                                    {j.qa.map((q) => (
                                                        <Box key={q.id}>
                                                            <HStack spacing={2} flexWrap="wrap" mb={2}>
                                                                <Badge colorScheme={QA_COLOR[q.ai_approval_status] || 'gray'}>{q.ai_quality_score ?? '—'} · {human(q.ai_approval_status)}</Badge>
                                                                <Badge variant="outline">{human(q.workmanship_rating)}</Badge>
                                                                <Text fontSize="xs" color={muted}>{fmtWhen(q.audited_at)}{q.audited_by?.name ? ` by ${q.audited_by.name}` : ''}{q.contractor_name ? ` • ${q.contractor_name}` : ''}</Text>
                                                            </HStack>
                                                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
                                                                <Field label="IRI (m/km)" colors={colors}>{q.iri ?? '—'}</Field>
                                                                <Field label="Compaction" colors={colors}>{q.density_pct !== null ? `${q.density_pct}%` : '—'}</Field>
                                                                <Field label="Thermal" colors={colors}>{q.thermal_score !== null ? `${q.thermal_score}%` : '—'}</Field>
                                                                <Field label="Material variance" colors={colors}>{q.material_variance_pct !== null ? `${q.material_variance_pct}%` : '—'}</Field>
                                                            </SimpleGrid>
                                                            {q.reasoning && <Text fontSize="xs" mt={2} whiteSpace="pre-wrap">{q.reasoning}</Text>}
                                                            {q.notes && <Text fontSize="xs" mt={1} color={muted}>{q.notes}</Text>}
                                                        </Box>
                                                    ))}
                                                </VStack>
                                            </Section>
                                        )}

                                        {j.asphalt && (
                                            <Section icon={MdLocalShipping} title="Asphalt deliveries" colors={colors}>
                                                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
                                                    <Field label="Delivered" colors={colors}>{j.asphalt.tons} t</Field>
                                                    <Field label="Trucks" colors={colors}>{j.asphalt.trucks}</Field>
                                                    <Field label="Cold loads" colors={colors}>{j.asphalt.cold ? <Text as="span" color="red.400">{j.asphalt.cold}</Text> : 0}</Field>
                                                    <Field label="Lowest arrival" colors={colors}>{j.asphalt.min_arrival_c !== null ? `${j.asphalt.min_arrival_c} °C` : '—'}</Field>
                                                </SimpleGrid>
                                            </Section>
                                        )}

                                        {(j.parts || []).length > 0 && (
                                            <Section icon={MdInventory2} title="Parts & materials used" colors={colors}>
                                                <Box overflowX="auto">
                                                    <Table size="sm">
                                                        <Thead><Tr><Th>Item</Th><Th>SKU</Th><Th isNumeric>Qty</Th><Th isNumeric>Unit</Th><Th isNumeric>Total</Th></Tr></Thead>
                                                        <Tbody>
                                                            {j.parts.map((p, i) => (
                                                                <Tr key={`${p.item_id}-${i}`}>
                                                                    <Td fontSize="xs">{p.name}</Td><Td fontSize="xs" fontFamily="mono">{p.sku || '—'}</Td>
                                                                    <Td isNumeric fontSize="xs">{p.quantity}</Td><Td isNumeric fontSize="xs">{money(p.unit_price)}</Td><Td isNumeric fontSize="xs">{money(p.line_total)}</Td>
                                                                </Tr>
                                                            ))}
                                                        </Tbody>
                                                    </Table>
                                                </Box>
                                            </Section>
                                        )}

                                        <Section icon={MdVerified} title={`Evidence score ${ev.score}/100`} colors={colors}>
                                            <Text fontSize="xs" color={muted} mb={1}>How FlowSense scores the paperwork behind this job (starts at 100).</Text>
                                            {ev.reasons.length ? ev.reasons.map((r) => <Text key={r.why} fontSize="xs">{r.points} {r.why}</Text>)
                                                : <Text fontSize="xs" color="green.500">No deductions — photos, checklist and notes are all in place.</Text>}
                                        </Section>
                                    </VStack>
                                </DrawerBody>
                                <DrawerFooter borderTopWidth="1px" gap={2}>
                                    <Button size="sm" variant="ghost" onClick={() => setSelected(null)}>Close</Button>
                                    <Button size="sm" colorScheme="blue" leftIcon={<Icon as={MdBuild} />} onClick={() => setWoModalJob(j)}>Open work order</Button>
                                </DrawerFooter>
                            </>
                        );
                    })()}
                </DrawerContent>
            </Drawer>

            {/* ── Inspection detail ──────────────────────────────────────────── */}
            <Drawer isOpen={!!selectedInsp} onClose={() => setSelected(null)} size={drawerSize} placement="right">
                <DrawerOverlay />
                <DrawerContent data-testid="cw-inspection-drawer">
                    <DrawerCloseButton />
                    {selectedInsp && (
                        <>
                            <DrawerHeader borderBottomWidth="1px" pr={10}>
                                <Text fontSize="md" fontWeight="800">{selectedInsp.template_label || selectedInsp.template_key}</Text>
                                <HStack spacing={1.5} mt={1} flexWrap="wrap">
                                    <Text fontSize="xs" fontFamily="mono" color={muted}>{selectedInsp.id}</Text>
                                    <Badge colorScheme={RATING_COLOR[selectedInsp.rating] || 'gray'}>{selectedInsp.score ?? '—'}{selectedInsp.rating ? ` · ${selectedInsp.rating}` : ''}</Badge>
                                </HStack>
                            </DrawerHeader>
                            <DrawerBody py={4}>
                                <VStack align="stretch" spacing={4}>
                                    <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                                        <Field label="Inspector" colors={colors}>{selectedInsp.submitted_by?.name || (selectedInsp.submitted_by?.id ? `User ${selectedInsp.submitted_by.id}` : '—')}</Field>
                                        <Field label="Submitted" colors={colors}>{fmtWhen(selectedInsp.submitted_at)}</Field>
                                        <Field label="Started" colors={colors}>{fmtWhen(selectedInsp.created_at)}</Field>
                                        <Field label="Work order" colors={colors}>{selectedInsp.work_order_title || selectedInsp.work_order_id || '—'}</Field>
                                        <Field label="Raised job" colors={colors}>{selectedInsp.outcome?.auto_work_order_id || selectedInsp.outcome?.duplicate_of || '—'}</Field>
                                        <Field label="Distress" colors={colors}>{selectedInsp.distress_types?.length ? selectedInsp.distress_types.map(human).join(', ') : '—'}</Field>
                                    </SimpleGrid>
                                    <ChecklistFindings checklist={selectedInsp} colors={colors} onOpenPhoto={(ph) => openPhotos(selectedInsp.photos || [], ph)}
                                        onOpenChecklist={onOpenChecklist ? (ck) => openChecklist(ck) : undefined} />
                                </VStack>
                            </DrawerBody>
                            <DrawerFooter borderTopWidth="1px"><Button size="sm" variant="ghost" onClick={() => setSelected(null)}>Close</Button></DrawerFooter>
                        </>
                    )}
                </DrawerContent>
            </Drawer>

            {lightbox && lightbox.photos.length > 0 && (
                <PhotoLightbox
                    photos={lightbox.photos}
                    index={Math.min(lightbox.index, lightbox.photos.length - 1)}
                    onClose={() => setLightbox(null)}
                    onIndex={(i) => setLightbox((l) => (l ? { ...l, index: i } : l))}
                    itemLabels={{}}
                    uploaderName={uploaderName}
                    metaFor={photoMeta}
                    colors={colors}
                />
            )}

            {woModalJob && (
                <ViewWorkOrderModal
                    isOpen
                    onClose={() => setWoModalJob(null)}
                    workOrder={{ ...woModalJob, assigned_user_username: woModalJob.assigned_user?.name || woModalJob.assigned_user_username, road_id: woModalJob.road_id ?? (road?.id || roadId) }}
                    onUpdate={() => { load(true); onDataChanged?.(); }}
                />
            )}
        </VStack>
    );
}

RoadCompletedWorkPanel.propTypes = {
    roadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    roadName: PropTypes.string,
    road: PropTypes.object,
    knownWorkOrderIds: PropTypes.array,
    focusJobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onFocusHandled: PropTypes.func,
    onOpenChecklist: PropTypes.func,
    onDataChanged: PropTypes.func,
};

export default memo(RoadCompletedWorkPanel);
